import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  contentChildren,
  effect,
  input,
  model,
  output,
  signal,
  untracked
} from '@angular/core';
import { merge, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { SelectionChangeEvent } from '../../domain/selectable.domain';
import { TabComponent } from './tab.component';

import type {
  AfterContentInit,
  InputSignal,
  ModelSignal,
  OnDestroy,
  OutputEmitterRef,
  Signal,
  WritableSignal
} from '@angular/core';
import type { SelectableTab, TabAsyncSelectEvent, TabsPosition, TabStatusChangeEvent, TabsType } from './tabs.domain';

/**
 * The Tabs Component
 *
 * This is the parent component to the [Tab Component]{@link TabComponent}.
 */
@Component({
  selector: 'td-wb-tabs',
  templateUrl: 'tabs.component.html',
  styleUrls: ['tabs.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'tdWbTabs',
  host: {
    '[attr.role]': 'role()',
    '[attr.aria-label]': 'ariaLabel()',
    '[attr.aria-labelledby]': 'ariaLabelledBy()'
  }
})
export class TabsComponent<T> implements AfterContentInit, OnDestroy {
  /**
   * The type of tabs to be used. Primary or secondary - defaults to primary
   */
  readonly type: InputSignal<TabsType> = input<TabsType>('primary');

  /**
   * The position type, either left, right or stretch, of each tab
   **/
  readonly position: InputSignal<TabsPosition> = input<TabsPosition>('start');

  /**
   * The selected tab's id. This is matched by [Tab Component's getTabId method]{@link TabComponent#getTabId}.
   */
  readonly selectedTabId: ModelSignal<T> = model<T>(undefined);

  /**
   * The aria label for the tabs.
   */
  readonly ariaLabel: InputSignal<string> = input<string>();
  readonly ariaLabelledBy: InputSignal<string> = input<string>();

  /**
   * The aria label for the tabs toolbar.
   */
  readonly ariaLabelToolbar: InputSignal<string> = input<string>();

  readonly disabled: InputSignal<boolean> = input<boolean>(false);

  readonly asyncSelect: ModelSignal<boolean> = model<boolean>(false, { alias: 'asyncSelect' });

  readonly onAsyncSelect: OutputEmitterRef<TabAsyncSelectEvent<T>> = output<TabAsyncSelectEvent<T>>();

  readonly onStatusChange: OutputEmitterRef<TabStatusChangeEvent<T>> = output<TabStatusChangeEvent<T>>();

  /**
   * This is a forward reference to the children of this component, which is a list of [Tab Components]{@link TabComponent}.
   */
  readonly tabs: Signal<ReadonlyArray<TabComponent<T>>> = contentChildren(TabComponent);

  readonly role: WritableSignal<string | null> = signal<string | null>(null);

  private initialSelectedTabId: T | undefined = undefined;
  private readonly tabsChange: Subject<void> = new Subject<void>();
  private readonly destroy: Subject<void> = new Subject<void>();

  constructor() {
    effect(() => {
      const tabs = this.tabs();
      const type = this.type();
      const selectedTabId = this.selectedTabId();

      // Ensure the effect re-runs if any tab's ID changes
      tabs.forEach((tab: TabComponent<T>) => tab.getTabId());

      untracked(() => {
        this.initialSelectedTabId ??= selectedTabId;
        this.updateTabs(tabs, type, selectedTabId);
      });
    });

    effect(() => {
      const tabs = this.tabs();
      untracked(() => {
        this.onTabListUpdate(tabs);
      });
    });

    afterNextRender(() => {
      if (this.initialSelectedTabId &&
          this.initialSelectedTabId !== this.selectedTabId() &&
          this.findTabById(this.initialSelectedTabId)) {
        this.setSelectedTab(this.initialSelectedTabId);
      }
    });
  }

  ngAfterContentInit(): void {
    if (this.ariaLabel() !== undefined || this.ariaLabelledBy() !== undefined) {
      this.role.set('navigation');
    }
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  /**
   * Sets the tab to selected state by calling [setSelected]{@link TabComponent#setSelected}
   * Emits selectedTabIdChange to show the selectedTabId has changed.
   *
   * @param tabId
   @param fromView
   */
  selectTab(tabId: T, fromView: boolean = false): void {
    this.tabs().forEach((tab: TabComponent<T>): void => {
      tab.setSelected(tab.getTabId() === tabId);
    });
    this.selectedTabId.set(tabId);
    this.onStatusChange.emit({ tabId, eventType: 'afterSelect', fromView });
  }

  resetAsyncSelect(): void {
    let tabIdToSelect: T;
    this.tabs().forEach((tab: TabComponent<T>): void => {
      if (tab.selected() && !tab.asyncSelect()) {
        tabIdToSelect = tab.getTabId();
      }
      tab.asyncSelect.set(false);
    });
    if (tabIdToSelect !== undefined) {
      this.selectTab(tabIdToSelect);
    }
  }

  getTabsClass(): string {
    return `td-wb-tabs td-wb-tabs--${this.type()} td-wb-tabs--${this.position()}`;
  }

  /**
   * This function is used to find a tab based on the provided id.
   *
   * @param id The id that is used to find the correct tab.
   * @returns The tab it found.
   */
  private findTabById(id: unknown): TabComponent<T> | undefined {
    return this.tabs().find((tab: TabComponent<T>): boolean => tab.getTabId() === id);
  }

  /**
   * Every time collection of tab changes we need to subscribe to the tab selection events emitted by every tab.
   */
  private onTabListUpdate(tabs: ReadonlyArray<TabComponent<T>>): void {
    // We need to clear previous subscriptions if any.
    this.tabsChange.next();
    const changedOrDestroyed = merge(this.tabsChange, this.destroy);

    merge(...tabs.map((tab: TabComponent<T>) => tab.onSelectionChange))
      .pipe(
        takeUntil(changedOrDestroyed),
        filter((event: SelectionChangeEvent<SelectableTab>): boolean => event.source.isSelected() && !this.disabled())
      ).subscribe((event: SelectionChangeEvent<SelectableTab>): void => {
        this.setSelectedTab(event.source.id as T, event.fromView);
      });
  }

  /**
   * Sets properties such as first tabs type and assigns default ids to each tab
   */
  private updateTabs(tabs: ReadonlyArray<TabComponent<T>>, type: TabsType, selectedTabId: T): void {
    tabs.forEach((tab: TabComponent<T>, index: number): void => {
      tab.setFirst(index === 0);
      tab.setTabsType(type);
      if (tab.getTabId() === undefined) {
        tab.setTabId(`${index}` as T);
      }
      if (selectedTabId === tab.getTabId() && !tab.isSelected()) {
        this.setSelectedTab(tab.getTabId());
      }
    });

    if (tabs.length > 0) {
      const selectedTab = this.findTabById(selectedTabId);
      if (selectedTab === undefined && !this.asyncSelect()) {
        this.setSelectedTab(tabs[0].tabId());
      }
    }
  }

  private setSelectedTab(tabId: T, fromView: boolean = false): void {
    this.onStatusChange.emit({ tabId, eventType: 'beforeSelect', fromView });
    if (this.asyncSelect()) {
      const tab = this.findTabById(tabId);
      if (tab) {
        tab.asyncSelect.set(true);
        this.onAsyncSelect.emit({
          select: (): void => {
            tab.asyncSelect.set(false);
            this.selectTab(tabId, fromView);
          },
          tabId
        });
      }
    } else {
      this.selectTab(tabId, fromView);
    }
  }
}
